import {
  WorksheetCostCategory,
  WorksheetCoverageUnit,
  WorksheetDensityUnit,
  WorksheetHeaderUnit,
  WorksheetLabourUnit,
  WorksheetMaterialCalcMethod,
  WorksheetMaterialOutputUnit,
  WorksheetMaterialUnit,
  WorksheetPricingMode,
  WorksheetThicknessUnit,
} from "generated/graphql";
import * as z from "zod";

export const WorksheetProductionHelperResolver = z.object({
  enabled: z.boolean(),
  source: z.string(),
  materialRowUuid: z.string(),
  quantity: z.string(),
  unit: z.string(),
  rate: z.string(),
  period: z.string(),
});

export const WorksheetTravelHelperResolver = z.object({
  enabled: z.boolean(),
  roundTripHours: z.string(),
  visits: z.string(),
});

export const WorksheetHelpersResolver = z.object({
  calculationVersion: z.number().nullable().optional(),
  hoursPerDay: z.string(),
  production: WorksheetProductionHelperResolver,
  travel: WorksheetTravelHelperResolver,
});

export const WorksheetCostRowInputResolver = z.object({
  id: z.string().optional(),
  category: z.nativeEnum(WorksheetCostCategory),
  name: z.string().min(1, "Name is required"),
  sourceType: z.string(),
  sourceId: z.string(),
  sourceName: z.string(),
  sourcePlantId: z.string(),
  sourcePlantName: z.string(),
  quantity: z.string(),
  unit: z.union([
    z.nativeEnum(WorksheetLabourUnit),
    z.nativeEnum(WorksheetMaterialUnit),
    z.literal(""),
  ]),
  rate: z.string(),
  trucksNeeded: z.string(),
  hoursPerTruck: z.string(),
  truckingUnit: z.string(),
  truckCapacity: z.string(),
  rowUuid: z.string(),
  quantitySource: z.string(),
  haulMaterialRowUuid: z.string(),
  haulAllocationPercent: z.string(),
  haulCapacityUnit: z.string(),
  haulRoundTripHours: z.string(),
  haulLoadHours: z.string(),
  haulUnloadHours: z.string(),
  haulPlanningHours: z.string(),
  haulMaxTripsPerDay: z.string(),
  haulQuantityMode: z.string(),
  haulQuantity: z.string(),
  haulUnit: z.string(),
  haulFullLoads: z.boolean(),
  truckRateHours: z.string(),
  truckRateTons: z.string(),
  truckRateYards: z.string(),
  truckRateLoads: z.string(),
  calcMethod: z.nativeEnum(WorksheetMaterialCalcMethod),
  jobSizeOverride: z.string(),
  jobSizeUnit: z.union([z.nativeEnum(WorksheetHeaderUnit), z.literal("")]),
  coverageRate: z.string(),
  coverageUnit: z.nativeEnum(WorksheetCoverageUnit),
  thickness: z.string(),
  thicknessUnit: z.nativeEnum(WorksheetThicknessUnit),
  density: z.string(),
  densityUnit: z.nativeEnum(WorksheetDensityUnit),
  weightFactor: z.string(),
  outputUnit: z.nativeEnum(WorksheetMaterialOutputUnit),
  quantityOverridden: z.boolean(),
});

export const WorksheetPricingBaselineResolver = z.object({
  pricingMode: z.nativeEnum(WorksheetPricingMode),
  sellingRate: z.string(),
  fixedTotal: z.string(),
  markupPercentage: z.string(),
});

export const WorksheetItemInputResolver = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Item name is required"),
  statementOfWork: z.string(),
  unit: z.nativeEnum(WorksheetHeaderUnit),
  sellingQuantity: z.string(),
  sellingRate: z.string(),
  fixedTotal: z.string(),
  pricingMode: z.nativeEnum(WorksheetPricingMode),
  pricingOverrideField: z.string().optional(),
  pricingBaseline: WorksheetPricingBaselineResolver.nullable().optional(),
  markupPercentage: z.string(),
  overheadPercentage: z.string(),
  indirectPercentage: z.string(),
  isOptional: z.boolean(),
  organizationProductTypeId: z.string().optional(),
  internalName: z.string(),
  discount: z.string(),
  helpers: WorksheetHelpersResolver,
  costRows: z.array(WorksheetCostRowInputResolver),
});

export type WorksheetItemInput = z.infer<typeof WorksheetItemInputResolver>;

export type WorksheetPricingBaselineFormValues = z.infer<typeof WorksheetPricingBaselineResolver>;

export type WorksheetHelpersFormValues = z.infer<typeof WorksheetHelpersResolver>;

export type WorksheetCostRowInput = WorksheetItemInput["costRows"][number];
